// *** Angular
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { first } from 'rxjs/operators';

// *** Components
import { EditStudentComponent } from '../modals/edit-student/edit-student.component';

// *** Actions
import * as studentActions from '../state/student/action/student.actions';
import * as facultyActions from '../state/faculty/action/faculty.actions';

// *** Selectors
import { getInstitutionStudentsStateDetails } from '../state/student/selector/student.selectors';
import { getInstitutionFacultiesStateDetails } from '../state/faculty/selector/faculty.selectors';

// *** Packages
import swal from 'sweetalert2';

// *** Services
import { DepartmentService } from 'src/app/services/department.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { ExcelExportService } from 'src/app/services/excel-export.service';
import { PdfExportService } from 'src/app/services/pdf-export.service';
import { UserService } from 'src/app/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { StudentFormRegistrationComponent } from '../student-form-registration/student-form-registration.component';
import { TableColumn } from 'src/app/shared/components/app-table/app-table.component';

@Component({
   selector: 'app-units-students',
   templateUrl: './units-students.component.html',
   styleUrls: ['./units-students.component.scss'],
})
export class UnitsStudentsComponent implements OnInit, OnDestroy {
   /**
    * Variable is used to store retrieved students from service.
    */
   students: any;
   students$;
   /**
    * Symbolizing the page of pagination
    */
   page = 1;
   /**
    * Variabel used to store total number of students without pagination
    */
   studentsNumber: number;
   /**
    * Variable is used to store facultyId to filter students of specific faculty.
    */
   facultyId = '-1';
   /**
    * Variable is used to store departmentId that will be used to filter students of specific department
    */
   departmentId = '-1';
   /**
    * Variable used to store name of student that used to filter them
    */
   name: any;
   /**
    * Variable used to store a time for function to trigger.(x time after key is up call y function.)
    */
   timer: any;
   /**
    * Variable is used to store departments of selected faculty
    */
   departments: any;
   /**
    * Variabels used to retrieve faculties of insitution from store.
    */
   faculties;
   getInstitutionFacultiesStateDetails$: any;
   @ViewChild('contentToCapture') contentToCapture: ElementRef;

   // Table columns configuration
   columns: TableColumn[] = [
      { key: 'name', label: 'general.name' },
      { key: 'email', label: 'general.email' },
      { key: 'indexId', label: 'general.student_index_id' },
      { key: 'faculty', label: 'app.institution' },
      { key: 'department', label: 'general.department' },
      { key: 'status', label: 'general.status', type: 'template', templateKey: 'statusTemplate' },
      { key: 'edit', label: 'general.edit', type: 'template', templateKey: 'editTemplate' }
   ];

   // Table data for app-table
   tableData: any[] = [];

   constructor(
      public dialog: MatDialog,
      private store: Store,
      private departmentService: DepartmentService,
      private csvExportService: CsvExportService,
      private excelExportService: ExcelExportService,
      private pdfExportService: PdfExportService,
      private spinner: NgxSpinnerService,
      private userService: UserService,
      private translate: TranslateService
   ) { }
   ngOnDestroy(): void {
      this.getInstitutionFacultiesStateDetails$.unsubscribe();
      this.students$.unsubscribe();
   }
   ngOnInit(): void {
      this.store.dispatch(facultyActions.loadFacultiesDetails({ page: 0 }));

      this.getInstitutionFacultiesStateDetails$ = this.store
         .select(getInstitutionFacultiesStateDetails)
         .subscribe((data) => {
            if (data !== null) {
               this.faculties = data.faculties;
            }
         });

      this.store.dispatch(
         studentActions.loadStudentsDetails({
            page: this.page,
            faculty: this.facultyId == '-1' ? undefined : this.facultyId,
            department:
               this.departmentId == '-1' ? undefined : this.departmentId,
            name: this.name,
         })
      );

      this.students$ = this.store
         .select(getInstitutionStudentsStateDetails)
         .subscribe((data) => {
            if (data !== null) {
               this.students = data.students;
               this.studentsNumber = data.studentsNumber;
               // Map students data to table format
               if (this.students) {
                  this.tableData = this.students.map(student => this.mapStudentToTableRow(student));
               }
            }
            if (data.studentRegistered !== null) {
               this.store.dispatch(studentActions.setStudentRegisterToNull());
               this.resetFilter();
            }
         });
   }

   /**
    * Maps student data to table row format
    * @param student - The student object to map
    * @returns Mapped table row object
    */
   mapStudentToTableRow(student: any) {
      return {
         id: student.id,
         name: student.name || '-',
         email: student.email || '-',
         indexId: student.indexId || '-',
         faculty: student.Faculty?.name || '-',
         department: student.Department?.name || '-',
         status: student.status,
         // Store original student data for modal
         originalStudent: student
      };
   }

   /**
    * Method is used to open a modal that will contain a student information to edit or delete.
    * @param id
    */
   openModal(id) {
      this.dialog.open(EditStudentComponent, {
         data: {
            studentId: id,
            page: this.page,
            faculty: this.facultyId == '-1' ? undefined : this.facultyId,
            department:
               this.departmentId == '-1' ? undefined : this.departmentId,
            name: this.name,
         },
         width: '50%',
         height: '95%',
      });
   }

   /**
    * Method is used to open a modal that will contain a student information to register students.
    */
   registerStudentModal() {
      this.dialog.open(StudentFormRegistrationComponent, {
         width: '50%',
         height: '95%',
      });
   }

   /**
    * Method is used to delete a student.
    * @param studentId - The ID of the student to delete
    */
   async deleteStudent(studentId: string) {
      const result = await swal.fire({
         title: this.translate.instant('new_entries.delete_student_modal_title'),
         icon: 'warning',
         showCancelButton: true,
         confirmButtonColor: '#3085d6',
         cancelButtonColor: '#b5adad',
         confirmButtonText: this.translate.instant('app.confirm'),
         cancelButtonText: this.translate.instant('report.cancel'),
         allowOutsideClick: false,
      });
      if (result.isConfirmed) {
         this.store.dispatch(
            studentActions.removeStudent({
               studentId: studentId,
               page: this.page,
               faculty: this.facultyId == '-1' ? undefined : this.facultyId,
               department: this.departmentId == '-1' ? undefined : this.departmentId,
               name: this.name,
            })
         );
      }
   }

   /**
    * Method used to change pagination page. It dispatches an action with page as param.
    * @param event
    */
   changePage(event): void {
      this.page = event;
      this.store.dispatch(
         studentActions.loadStudentsDetails({
            page: this.page,
         })
      );
   }
   /**
    * Function is used to select a faculty to filter thesis and also get departments of selected faculty
    */
   selectedFaculty() {
      this.page = 1;
      this.departmentId = '-1';
      this.store.dispatch(
         studentActions.loadStudentsDetails({
            page: this.page,
            faculty: this.facultyId == '-1' ? undefined : this.facultyId,
            department:
               this.departmentId == '-1' ? undefined : this.departmentId,
            name: this.name,
         })
      );
      if (this.facultyId !== '') {
         this.departmentService
            .getDepartmentsOfFaculty(this.facultyId)
            .pipe(first())
            .subscribe(
               (data) => {
                  this.departments = data.departments;
               },
               (error) => {
                  console.log(error);
               }
            );
      }
   }
   /**
    * Function used to filter thesis by title
    */
   titleFilter() {
      let time = 500;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
         this.page = 1;
         this.store.dispatch(
            studentActions.loadStudentsDetails({
               page: this.page,
               faculty: this.facultyId == '-1' ? undefined : this.facultyId,
               department:
                  this.departmentId == '-1' ? undefined : this.departmentId,
               name: this.name,
            })
         );
      }, time);
   }
   /**
    * Function used to filter student by department
    */
   departmentFilter() {
      this.page = 1;
      this.store.dispatch(
         studentActions.loadStudentsDetails({
            page: this.page,
            faculty: this.facultyId == '-1' ? undefined : this.facultyId,
            department:
               this.departmentId == '-1' ? undefined : this.departmentId,
            name: this.name,
         })
      );
   }
   /**
    * Function is used to reset filters
    */
   resetFilter() {
      this.page = 1;
      this.facultyId = '-1';
      this.departmentId = '-1';
      this.name = undefined;
      this.store.dispatch(
         studentActions.loadStudentsDetails({
            page: this.page,
            faculty: this.facultyId == '-1' ? undefined : this.facultyId,
            department:
               this.departmentId == '-1' ? undefined : this.departmentId,
            name: this.name,
         })
      );
   }


   prepareExportData(data) {
      let rows = [
         ["Students"],
         [""],
         [
            "Student name",
            "Email",
            "Student Index ID",
            "Institution",
            "Department",
            "Status",
         ],
      ]

      data.students.forEach(student => {
         rows.push([
            student.name,
            student.email,
            student.indexId,
            student.Faculty.name,
            student.Department.name,
            student.status ? this.translate.instant('general.active') : 'Inactive',
         ]);
      });

      return rows
   }

   exportToCSV() {
      this.spinner.show();
      this.userService.institutionStudents(
         -1,
         this.facultyId == '-1' ? undefined : this.facultyId,
         this.departmentId == '-1' ? undefined : this.departmentId,
         this.name,
      ).subscribe((data) => {
         const rows = this.prepareExportData(data)
         this.csvExportService.exportToCSV(rows, 'Students');
         this.spinner.hide();
      })
   }

   exportToExcel() {
      this.spinner.show();
      this.userService.institutionStudents(
         -1,
         this.facultyId == '-1' ? undefined : this.facultyId,
         this.departmentId == '-1' ? undefined : this.departmentId,
         this.name,
      ).subscribe((data) => {
         const rows = this.prepareExportData(data)
         this.excelExportService.exportToExcel(rows, 'Students');
         this.spinner.hide();
      })
   }

   exportToPDF() {
      this.spinner.show()
      if (this.contentToCapture) {
         this.pdfExportService.captureAndExportPDF(this.contentToCapture.nativeElement, 'Students.pdf');
         this.spinner.hide()
      } else {
         console.error('Content to capture is not available yet.');
         this.spinner.hide()
      }
   }
}
